<template>
  <div class="api-integrations-container">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-plug mr-3"></i>
          API Integrations
        </h1>
        <p class="page-description">
          Connect external APIs like NCBI, PubMed, and ClinicalTrials.gov to enhance your document analysis with real-time research data.
        </p>
      </div>
      <button 
        @click="showCreateModal = true" 
        class="btn btn-primary btn-lg"
      >
        <i class="fas fa-plus mr-2"></i>
        Add API Connection
      </button>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        @click="activeTab = tab.id"
        class="tab-button"
        :class="{ 'active': activeTab === tab.id }"
      >
        <i :class="tab.icon"></i>
        {{ tab.name }}
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      
      <!-- Connections Tab -->
      <div v-if="activeTab === 'connections'" class="tab-pane">
        <!-- Connection Cards Grid -->
        <div class="connections-grid" v-if="connections.length > 0">
          <div 
            v-for="connection in connections" 
            :key="connection.id"
            class="connection-card"
            :class="{ 
              'status-active': connection.status === 'ACTIVE',
              'status-error': connection.status === 'ERROR',
              'status-pending': connection.status === 'PENDING',
              'status-rate-limited': connection.status === 'RATE_LIMITED'
            }"
          >
            <!-- Connection Header -->
            <div class="connection-header">
              <div class="connection-info">
                <h3 class="connection-name">{{ connection.name }}</h3>
                <p class="connection-description">{{ connection.description || 'No description' }}</p>
                <div class="connection-details">
                  <span class="detail-item">
                    <i class="fas fa-globe"></i>
                    {{ getAPIType(connection) }}
                  </span>
                  <span class="detail-item">
                    <i class="fas fa-link"></i>
                    {{ getDomainFromUrl(connection.base_url) }}
                  </span>
                  <span class="detail-item">
                    <i class="fas fa-shield-alt"></i>
                    {{ getAuthTypeDisplay(connection.auth_type) }}
                  </span>
                </div>
              </div>
              <div class="connection-status">
                <span 
                  class="status-badge"
                  :class="`status-${(connection.status || 'pending').toLowerCase()}`"
                >
                  {{ getStatusText(connection.status) }}
                </span>
              </div>
            </div>

            <!-- Connection Stats -->
            <div class="connection-stats" v-if="connection.status === 'ACTIVE'">
              <div class="stat-item">
                <span class="stat-label">Total Requests</span>
                <span class="stat-value">{{ connection.request_count || 0 }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Success Rate</span>
                <span class="stat-value">
                  {{ getSuccessRate(connection) }}%
                </span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Avg Response</span>
                <span class="stat-value">
                  {{ connection.avg_response_time ? Math.round(connection.avg_response_time) + 'ms' : 'N/A' }}
                </span>
              </div>
              <div class="stat-item" v-if="connection.rate_limit_per_hour">
                <span class="stat-label">Rate Limit</span>
                <span class="stat-value">
                  {{ connection.current_hour_requests || 0 }}/{{ connection.rate_limit_per_hour }}
                </span>
              </div>
            </div>

            <!-- Connection Actions -->
            <div class="connection-actions">
              <button 
                @click="testConnection(connection)"
                :disabled="testingConnections.includes(connection.id)"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fas fa-vial" v-if="!testingConnections.includes(connection.id)"></i>
                <i class="fas fa-spinner fa-spin" v-else></i>
                {{ testingConnections.includes(connection.id) ? 'Testing...' : 'Test' }}
              </button>
              
              <button 
                @click="discoverEndpoints(connection)"
                :disabled="discoveringEndpoints.includes(connection.id)"
                class="btn btn-sm btn-outline-info"
              >
                <i class="fas fa-search" v-if="!discoveringEndpoints.includes(connection.id)"></i>
                <i class="fas fa-spinner fa-spin" v-else></i>
                {{ discoveringEndpoints.includes(connection.id) ? 'Discovering...' : 'Discover' }}
              </button>

              <!-- NCBI-specific actions -->
              <button 
                v-if="isNCBIConnection(connection)"
                @click="openNCBISearchModal(connection)"
                class="btn btn-sm btn-outline-success"
              >
                <i class="fas fa-search-plus"></i>
                Search NCBI
              </button>

              <div class="dropdown" :class="{ 'show': openDropdowns.includes(connection.id) }">
                <button 
                  @click="toggleDropdown(connection.id)"
                  class="btn btn-sm btn-outline-secondary dropdown-toggle"
                >
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <div class="dropdown-menu" :class="{ 'show': openDropdowns.includes(connection.id) }">
                  <button @click="viewConnectionStats(connection)" class="dropdown-item">
                    <i class="fas fa-chart-bar mr-2"></i>View Stats
                  </button>
                  <button @click="viewRequestHistory(connection)" class="dropdown-item">
                    <i class="fas fa-history mr-2"></i>Request History
                  </button>
                  <div class="dropdown-divider"></div>
                  <button @click="editConnection(connection)" class="dropdown-item">
                    <i class="fas fa-edit mr-2"></i>Edit
                  </button>
                  <button @click="deleteConnection(connection)" class="dropdown-item text-danger">
                    <i class="fas fa-trash mr-2"></i>Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-state-content">
            <i class="fas fa-plug empty-state-icon"></i>
            <h3>No API Connections</h3>
            <p>Connect to external APIs to enhance your document analysis with real-time data from authoritative sources.</p>
            <button @click="showCreateModal = true" class="btn btn-primary">
              <i class="fas fa-plus mr-2"></i>
              Add Your First API Connection
            </button>
          </div>
        </div>
      </div>

      <!-- API Explorer Tab -->
      <div v-if="activeTab === 'explorer'" class="tab-pane">
        <div class="api-explorer-section">
          <h3><i class="fas fa-compass mr-2"></i>API Explorer</h3>
          <p>Test API endpoints and explore available data from your connected APIs.</p>
          
          <div v-if="connections.length === 0" class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            Please add an API connection first to use the explorer.
          </div>
          
          <div v-else class="explorer-interface">
            <!-- Connection Selector -->
            <div class="connection-selector mb-4">
              <label class="form-label">Select API Connection:</label>
              <select v-model="selectedConnection" class="form-select">
                <option value="">Choose a connection...</option>
                <option v-for="connection in connections" :key="connection.id" :value="connection">
                  {{ connection.name }} ({{ getAPIType(connection) }})
                </option>
              </select>
            </div>
            
            <!-- API Interface -->
            <div v-if="selectedConnection" class="api-form-section">
              
              <!-- NCBI-specific interface -->
              <div v-if="isNCBIConnection(selectedConnection)" class="ncbi-interface">
                <h4><i class="fas fa-dna mr-2"></i>NCBI E-utilities Interface</h4>
                
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Database:</label>
                      <select v-model="ncbiForm.database" class="form-select">
                        <option value="pubmed">PubMed</option>
                        <option value="pmc">PMC</option>
                        <option value="books">Books</option>
                        <option value="nuccore">Nucleotide</option>
                        <option value="protein">Protein</option>
                        <option value="genome">Genome</option>
                        <option value="structure">Structure</option>
                        <option value="taxonomy">Taxonomy</option>
                        <option value="snp">SNP</option>
                        <option value="clinvar">ClinVar</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">Max Results:</label>
                      <input 
                        v-model.number="ncbiForm.max_results" 
                        type="number" 
                        min="1" 
                        max="100" 
                        class="form-control"
                      >
                    </div>
                  </div>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Search Query:</label>
                  <input 
                    v-model="ncbiForm.query" 
                    type="text" 
                    class="form-control" 
                    placeholder="e.g., cancer treatment clinical trials"
                  >
                </div>
                
                <div class="form-actions">
                  <button 
                    @click="executeNCBISearch"
                    :disabled="executingRequest || !ncbiForm.query"
                    class="btn btn-primary"
                  >
                    <i class="fas fa-search" v-if="!executingRequest"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ executingRequest ? 'Searching...' : 'Search NCBI' }}
                  </button>
                  
                  <button 
                    @click="getNCBIDatabases"
                    :disabled="executingRequest"
                    class="btn btn-outline-info ml-2"
                  >
                    <i class="fas fa-database"></i>
                    List Databases
                  </button>
                </div>
              </div>

              <!-- Generic API interface -->
              <div v-else class="generic-interface">
                <h4><i class="fas fa-code mr-2"></i>Generic API Interface</h4>
                
                <div class="row">
                  <div class="col-md-8">
                    <div class="form-group">
                      <label class="form-label">Endpoint Path:</label>
                      <input 
                        v-model="apiForm.endpoint_path" 
                        type="text" 
                        class="form-control" 
                        placeholder="/api/endpoint"
                      >
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label">Method:</label>
                      <select v-model="apiForm.method" class="form-select">
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Parameters (JSON):</label>
                  <textarea 
                    v-model="apiForm.params_json" 
                    class="form-control" 
                    rows="4" 
                    placeholder='{"param1": "value1", "param2": "value2"}'
                  ></textarea>
                </div>
                
                <div class="form-actions">
                  <button 
                    @click="executeAPIRequest"
                    :disabled="executingRequest || !apiForm.endpoint_path"
                    class="btn btn-primary"
                  >
                    <i class="fas fa-play" v-if="!executingRequest"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ executingRequest ? 'Executing...' : 'Execute Request' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Request History Tab -->
      <div v-if="activeTab === 'history'" class="tab-pane">
        <div class="request-history-section">
          <h3><i class="fas fa-history mr-2"></i>Request History</h3>
          <p>View and analyze your API request history and performance metrics.</p>
          
          <div class="history-controls mb-4">
            <div class="row">
              <div class="col-md-4">
                <select v-model="historyFilter.connection" class="form-select">
                  <option value="">All Connections</option>
                  <option v-for="connection in connections" :key="connection.id" :value="connection.id">
                    {{ connection.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <select v-model="historyFilter.status" class="form-select">
                  <option value="">All Statuses</option>
                  <option value="success">Successful</option>
                  <option value="error">Failed</option>
                </select>
              </div>
              <div class="col-md-4">
                <button @click="loadRequestHistory" class="btn btn-outline-primary">
                  <i class="fas fa-refresh"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div class="request-history-table" v-if="requestHistory.length > 0">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Connection</th>
                  <th>Endpoint</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Response Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="request in requestHistory" :key="request.id">
                  <td>{{ formatTimestamp(request.executed_at) }}</td>
                  <td>{{ request.connection_name }}</td>
                  <td class="endpoint-cell">{{ request.endpoint }}</td>
                  <td>
                    <span class="method-badge" :class="`method-${request.method.toLowerCase()}`">
                      {{ request.method }}
                    </span>
                  </td>
                  <td>
                    <span class="status-badge" :class="request.success ? 'status-success' : 'status-error'">
                      {{ request.success ? 'Success' : 'Failed' }}
                    </span>
                  </td>
                  <td>{{ request.response_time_ms ? request.response_time_ms + 'ms' : 'N/A' }}</td>
                  <td>
                    <button @click="viewRequestDetails(request)" class="btn btn-sm btn-outline-info">
                      <i class="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty-state">
            <i class="fas fa-history empty-state-icon"></i>
            <h4>No Request History</h4>
            <p>API requests will appear here once you start using your connections.</p>
          </div>
        </div>
      </div>

      <!-- Analytics Tab -->
      <div v-if="activeTab === 'analytics'" class="tab-pane">
        <div class="analytics-section">
          <h3><i class="fas fa-chart-line mr-2"></i>API Analytics</h3>
          <p>Monitor API usage, performance, and trends across all your connections.</p>
          
          <div class="analytics-cards" v-if="analytics">
            <div class="analytics-card">
              <div class="card-header">
                <h4>Total Requests</h4>
                <i class="fas fa-exchange-alt"></i>
              </div>
              <div class="card-value">{{ analytics.total_requests || 0 }}</div>
              <div class="card-subtitle">Last 30 days: {{ analytics.recent_requests_30d || 0 }}</div>
            </div>
            
            <div class="analytics-card">
              <div class="card-header">
                <h4>Success Rate</h4>
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="card-value">{{ Math.round(analytics.success_rate || 0) }}%</div>
              <div class="card-subtitle">{{ analytics.successful_requests || 0 }} successful</div>
            </div>
            
            <div class="analytics-card">
              <div class="card-header">
                <h4>Avg Response Time</h4>
                <i class="fas fa-clock"></i>
              </div>
              <div class="card-value">{{ Math.round(analytics.avg_response_time_ms || 0) }}ms</div>
              <div class="card-subtitle">Across all connections</div>
            </div>
            
            <div class="analytics-card">
              <div class="card-header">
                <h4>Rate Limited</h4>
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="card-value">{{ Math.round(analytics.rate_limit_percentage || 0) }}%</div>
              <div class="card-subtitle">{{ analytics.rate_limited_requests || 0 }} requests</div>
            </div>
          </div>

          <div class="analytics-charts mt-4">
            <div class="row">
              <div class="col-md-6">
                <div class="chart-card">
                  <h5>Top Connections</h5>
                  <div class="connection-stats-list">
                    <div 
                      v-for="conn in analytics.top_connections" 
                      :key="conn.connection__name"
                      class="connection-stat-item"
                    >
                      <span class="connection-name">{{ conn.connection__name }}</span>
                      <span class="connection-count">{{ conn.request_count }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="chart-card">
                  <h5>Top Endpoints</h5>
                  <div class="endpoint-stats-list">
                    <div 
                      v-for="endpoint in analytics.top_endpoints" 
                      :key="endpoint.endpoint"
                      class="endpoint-stat-item"
                    >
                      <span class="endpoint-name">{{ endpoint.endpoint }}</span>
                      <span class="endpoint-count">{{ endpoint.count }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- GitHub Integration Tab -->
      <div v-if="activeTab === 'github'" class="tab-pane">
        <div class="github-section">
          <div class="github-section-header">
            <div>
              <h3><i class="fab fa-github mr-2"></i>GitHub Integration</h3>
              <p class="section-subtitle">Connect your GitHub account to pull commit history for invoices.</p>
            </div>
          </div>

          <!-- Step 1: Token Setup -->
          <div class="github-card">
            <div class="github-card-header">
              <h4 class="github-card-title">
                <i class="fas fa-key"></i> Step 1 — Personal Access Token
              </h4>
              <div v-if="githubConnectionExists" class="gh-connected-badge">
                <i class="fas fa-check-circle"></i> Connected
              </div>
            </div>

            <!-- Already connected — show summary -->
            <div v-if="githubConnectionExists && !showGitHubTokenForm" class="gh-token-summary">
              <p class="github-card-desc" style="margin-bottom:12px;">
                GitHub is connected. You can update the token below if needed.
              </p>
              <button class="btn-gh-secondary" @click="showGitHubTokenForm = true">
                <i class="fas fa-edit"></i> Update Token
              </button>
            </div>

            <!-- Inline token form -->
            <div v-if="!githubConnectionExists || showGitHubTokenForm" class="gh-token-form">
              <p class="github-card-desc">
                Generate a Personal Access Token at
                <a href="https://github.com/settings/tokens/new" target="_blank" class="gh-link">
                  github.com/settings/tokens <i class="fas fa-external-link-alt"></i>
                </a>
                — select <strong>repo</strong> scope (read access to commits).
              </p>
              <div class="gh-token-input-row">
                <div class="mapping-field" style="flex:1;">
                  <label>Personal Access Token</label>
                  <input
                    v-model="githubTokenInput"
                    type="password"
                    class="gh-input"
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                    autocomplete="new-password"
                  />
                </div>
                <div class="mapping-field" style="width:200px;">
                  <label>Connection Name</label>
                  <input
                    v-model="githubConnectionName"
                    type="text"
                    class="gh-input"
                    placeholder="GitHub"
                  />
                </div>
              </div>
              <div class="gh-token-actions">
                <button
                  class="btn-gh-primary"
                  @click="saveGitHubToken"
                  :disabled="!githubTokenInput || savingGitHubToken"
                >
                  <i :class="savingGitHubToken ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
                  {{ savingGitHubToken ? 'Saving...' : (githubConnectionExists ? 'Update Token' : 'Save Token') }}
                </button>
                <button v-if="showGitHubTokenForm" class="btn-gh-secondary" @click="showGitHubTokenForm = false">
                  Cancel
                </button>
              </div>
              <div v-if="githubTokenError" class="import-error" style="margin-top:10px;">{{ githubTokenError }}</div>
            </div>
          </div>

          <!-- Step 2: Repo-to-Client Mappings -->
          <div class="github-card">
            <div class="github-card-header">
              <h4 class="github-card-title">
                <i class="fas fa-code-branch"></i> Step 2 — Map Repos to Clients
              </h4>
              <button class="btn-gh-primary btn-sm-gh" @click="showAddMapping = true">
                <i class="fas fa-plus"></i> Add Mapping
              </button>
            </div>
            <p class="github-card-desc">
              Map each GitHub repository to the client organization it belongs to. When generating an invoice, all mapped repos will be queried automatically.
            </p>

            <!-- Add Mapping Form -->
            <div v-if="showAddMapping" class="add-mapping-form">
              <div class="mapping-form-row">
                <div class="mapping-field">
                  <label>Repository (owner/repo)</label>
                  <input v-model="newMapping.repo_full_name" type="text" class="gh-input" placeholder="e.g. my-org/my-project" />
                </div>
                <div class="mapping-field">
                  <label>Client Organization</label>
                  <select v-model="newMapping.client_organization" class="gh-input">
                    <option value="">Select client...</option>
                    <option v-for="client in invoicingClients" :key="client.id" :value="client.id">
                      {{ client.name }}
                    </option>
                  </select>
                </div>
                <div class="mapping-field">
                  <label>Display Name (optional)</label>
                  <input v-model="newMapping.display_name" type="text" class="gh-input" placeholder="Friendly name for invoices" />
                </div>
              </div>
              <div class="mapping-form-actions">
                <button class="btn-gh-primary" @click="saveMapping" :disabled="savingMapping">
                  <i :class="savingMapping ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
                  {{ savingMapping ? 'Saving...' : 'Save Mapping' }}
                </button>
                <button class="btn-gh-secondary" @click="showAddMapping = false">Cancel</button>
              </div>
            </div>

            <!-- Mappings Table -->
            <div v-if="githubMappings.length > 0" class="mappings-table-wrapper">
              <table class="mappings-table">
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Client</th>
                    <th>Display Name</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="mapping in githubMappings" :key="mapping.id">
                    <td><code>{{ mapping.repo_full_name }}</code></td>
                    <td>{{ mapping.client_organization_name }}</td>
                    <td>{{ mapping.display_name || '—' }}</td>
                    <td>
                      <span :class="mapping.is_active ? 'active-dot' : 'inactive-dot'">
                        {{ mapping.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td>
                      <button class="btn-gh-danger-sm" @click="deleteMapping(mapping.id)" title="Remove">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else-if="!loadingMappings" class="gh-empty">
              No repo mappings yet. Add one above to get started.
            </div>
            <div v-if="loadingMappings" class="gh-empty">Loading mappings...</div>
          </div>

          <!-- Quick Link to Invoicing -->
          <div class="github-card github-quick-link">
            <i class="fas fa-file-invoice-dollar"></i>
            <div>
              <strong>Ready to invoice?</strong>
              <p>Once your GitHub token and repo mappings are set up, head to Invoices to create your first invoice.</p>
            </div>
            <router-link to="/invoicing" class="btn-gh-primary">
              Go to Invoices <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Connection Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Add API Connection</h3>
          <button @click="closeCreateModal" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="createConnection">
            <!-- Template Selection -->
            <div class="form-group">
              <label class="form-label">API Template:</label>
              <select v-model="newConnection.api_template" class="form-select" required>
                <option value="">Select an API template...</option>
                <option v-for="template in apiTemplates" :key="template.id" :value="template.id">
                  {{ template.name }} - {{ template.description }}
                </option>
              </select>
            </div>
            
            <!-- Basic Information -->
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Connection Name:</label>
                  <input 
                    v-model="newConnection.name" 
                    type="text" 
                    class="form-control" 
                    required
                    placeholder="My NCBI Connection"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Base URL:</label>
                  <input 
                    v-model="newConnection.base_url" 
                    type="url" 
                    class="form-control" 
                    required
                    placeholder="https://api.example.com/"
                  >
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label class="form-label">Description:</label>
              <textarea 
                v-model="newConnection.description" 
                class="form-control" 
                rows="3"
                placeholder="Description of what this API provides..."
              ></textarea>
            </div>
            
            <!-- Authentication -->
            <div class="form-group">
              <label class="form-label">Authentication Type:</label>
              <select v-model="newConnection.auth_type" class="form-select" required>
                <option value="none">No Authentication</option>
                <option value="api_key">API Key</option>
                <option value="bearer_token">Bearer Token</option>
                <option value="basic_auth">Basic Authentication</option>
                <option value="oauth2">OAuth 2.0</option>
              </select>
            </div>
            
            <!-- Credentials based on auth type -->
            <div v-if="newConnection.auth_type === 'api_key'" class="form-group">
              <label class="form-label">API Key:</label>
              <input 
                v-model="newConnection.credentials.api_key" 
                type="password" 
                class="form-control"
                placeholder="Your API key"
              >
            </div>
            
            <div v-if="newConnection.auth_type === 'bearer_token'" class="form-group">
              <label class="form-label">Bearer Token:</label>
              <input 
                v-model="newConnection.credentials.token" 
                type="password" 
                class="form-control"
                placeholder="Your bearer token"
              >
            </div>
            
            <div v-if="newConnection.auth_type === 'basic_auth'" class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Username:</label>
                  <input 
                    v-model="newConnection.credentials.username" 
                    type="text" 
                    class="form-control"
                    placeholder="Username"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Password:</label>
                  <input 
                    v-model="newConnection.credentials.password" 
                    type="password" 
                    class="form-control"
                    placeholder="Password"
                  >
                </div>
              </div>
            </div>

            <!-- NCBI-specific fields -->
            <div v-if="selectedTemplate && selectedTemplate.api_type === 'NCBI_EUTILS'" class="ncbi-fields">
              <div class="alert alert-info">
                <i class="fas fa-info-circle mr-2"></i>
                For NCBI E-utilities, providing your email and tool name is required. An API key is optional but recommended for higher rate limits.
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label">Email (Required):</label>
                    <input 
                      v-model="newConnection.credentials.email" 
                      type="email" 
                      class="form-control"
                      required
                      placeholder="your.email@domain.com"
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label">Tool Name (Required):</label>
                    <input 
                      v-model="newConnection.credentials.tool" 
                      type="text" 
                      class="form-control"
                      required
                      placeholder="YourAppName"
                    >
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Rate Limiting -->
            <div class="form-group">
              <label class="form-label">Request Timeout (seconds):</label>
              <input 
                v-model.number="newConnection.timeout_seconds" 
                type="number" 
                min="5" 
                max="300" 
                class="form-control"
              >
            </div>
          </form>
        </div>
        
        <div class="modal-footer">
          <button @click="closeCreateModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="createConnection" 
            :disabled="creatingConnection"
            class="btn btn-primary"
          >
            <i class="fas fa-plus" v-if="!creatingConnection"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            {{ creatingConnection ? 'Creating...' : 'Create Connection' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Connection Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3><i class="fas fa-edit mr-2"></i>Edit API Connection</h3>
          <button @click="closeEditModal" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="updateConnection">
            <!-- Connection ID (read-only) -->
            <div class="form-group">
              <label class="form-label">Connection ID:</label>
              <input 
                :value="editConnectionForm.id" 
                type="text" 
                class="form-control" 
                disabled
              >
            </div>
            
            <!-- Basic Information -->
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Connection Name:</label>
                  <input 
                    v-model="editConnectionForm.name" 
                    type="text" 
                    class="form-control" 
                    required
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Base URL:</label>
                  <input 
                    v-model="editConnectionForm.base_url" 
                    type="url" 
                    class="form-control" 
                    required
                  >
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label class="form-label">Description:</label>
              <textarea 
                v-model="editConnectionForm.description" 
                class="form-control" 
                rows="3"
              ></textarea>
            </div>
            
            <!-- Authentication -->
            <div class="form-group">
              <label class="form-label">Authentication Type:</label>
              <select v-model="editConnectionForm.auth_type" class="form-select" required>
                <option value="none">No Authentication</option>
                <option value="api_key">API Key</option>
                <option value="bearer_token">Bearer Token</option>
                <option value="basic_auth">Basic Authentication</option>
                <option value="oauth2">OAuth 2.0</option>
              </select>
            </div>
            
            <!-- API Key Display/Edit -->
            <div v-if="editConnectionForm.auth_type === 'api_key'" class="form-group">
              <label class="form-label">
                API Key:
                <button 
                  v-if="editConnectionForm.credentials.api_key"
                  type="button" 
                  @click="toggleApiKeyVisibility" 
                  class="btn btn-sm btn-link ml-2"
                >
                  <i :class="editConnectionForm.showApiKey ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  {{ editConnectionForm.showApiKey ? 'Hide' : 'Show' }}
                </button>
              </label>
              <div class="input-group">
                <input 
                  v-model="editConnectionForm.credentials.api_key" 
                  :type="editConnectionForm.showApiKey ? 'text' : 'password'" 
                  class="form-control"
                  :placeholder="editConnectionForm.credentials.api_key ? 'Your API key' : 'Enter API key (existing key not retrievable)'"
                >
                <button 
                  v-if="editConnectionForm.credentials.api_key"
                  type="button" 
                  @click="copyToClipboard(editConnectionForm.credentials.api_key)"
                  class="btn btn-outline-secondary"
                  title="Copy to clipboard"
                >
                  <i class="fas fa-copy"></i>
                </button>
              </div>
              <small v-if="!editConnectionForm.credentials.api_key" class="form-text text-warning">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                Existing API key cannot be retrieved. Enter a new key to update, or leave blank to keep the existing key.
              </small>
              <small v-else class="form-text text-muted">
                Leave unchanged to keep existing key, or enter a new key to update.
              </small>
            </div>
            
            <!-- Bearer Token -->
            <div v-if="editConnectionForm.auth_type === 'bearer_token'" class="form-group">
              <label class="form-label">
                Bearer Token:
                <button 
                  v-if="editConnectionForm.credentials.token"
                  type="button" 
                  @click="toggleApiKeyVisibility" 
                  class="btn btn-sm btn-link ml-2"
                >
                  <i :class="editConnectionForm.showApiKey ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  {{ editConnectionForm.showApiKey ? 'Hide' : 'Show' }}
                </button>
              </label>
              <div class="input-group">
                <input 
                  v-model="editConnectionForm.credentials.token" 
                  :type="editConnectionForm.showApiKey ? 'text' : 'password'" 
                  class="form-control"
                  :placeholder="editConnectionForm.credentials.token ? 'Your bearer token' : 'Enter token (existing token not retrievable)'"
                >
                <button 
                  v-if="editConnectionForm.credentials.token"
                  type="button" 
                  @click="copyToClipboard(editConnectionForm.credentials.token)"
                  class="btn btn-outline-secondary"
                  title="Copy to clipboard"
                >
                  <i class="fas fa-copy"></i>
                </button>
              </div>
              <small v-if="!editConnectionForm.credentials.token" class="form-text text-warning">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                Existing token cannot be retrieved. Enter a new token to update, or leave blank to keep the existing token.
              </small>
            </div>
            
            <!-- Basic Auth -->
            <div v-if="editConnectionForm.auth_type === 'basic_auth'" class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Username:</label>
                  <input 
                    v-model="editConnectionForm.credentials.username" 
                    type="text" 
                    class="form-control"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Password:</label>
                  <input 
                    v-model="editConnectionForm.credentials.password" 
                    :type="editConnectionForm.showApiKey ? 'text' : 'password'" 
                    class="form-control"
                  >
                </div>
              </div>
            </div>
            
            <!-- NCBI-specific fields -->
            <div v-if="editConnectionForm.credentials.email || editConnectionForm.credentials.tool" class="ncbi-fields">
              <div class="alert alert-info">
                <i class="fas fa-info-circle mr-2"></i>
                NCBI E-utilities credentials
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label">Email:</label>
                    <input 
                      v-model="editConnectionForm.credentials.email" 
                      type="email" 
                      class="form-control"
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label">Tool Name:</label>
                    <input 
                      v-model="editConnectionForm.credentials.tool" 
                      type="text" 
                      class="form-control"
                    >
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Timeout -->
            <div class="form-group">
              <label class="form-label">Request Timeout (seconds):</label>
              <input 
                v-model.number="editConnectionForm.timeout_seconds" 
                type="number" 
                min="5" 
                max="300" 
                class="form-control"
              >
            </div>
          </form>
        </div>
        
        <div class="modal-footer">
          <button @click="closeEditModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="updateConnection" 
            :disabled="updatingConnection"
            class="btn btn-primary"
          >
            <i class="fas fa-save" v-if="!updatingConnection"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            {{ updatingConnection ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- NCBI Search Modal -->
    <div v-if="showNCBISearchModal" class="modal-overlay" @click="closeNCBISearchModal">
      <div class="modal-content large-modal" @click.stop>
        <div class="modal-header">
          <h3>NCBI Search - {{ selectedConnection?.name }}</h3>
          <button @click="closeNCBISearchModal" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="ncbi-search-form">
            <div class="row">
              <div class="col-md-8">
                <div class="form-group">
                  <label class="form-label">Search Query:</label>
                  <input 
                    v-model="ncbiSearchForm.query" 
                    type="text" 
                    class="form-control" 
                    placeholder="e.g., cancer treatment clinical trials"
                  >
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label class="form-label">Database:</label>
                  <select v-model="ncbiSearchForm.database" class="form-select">
                    <option value="pubmed">PubMed</option>
                    <option value="pmc">PMC</option>
                    <option value="books">Books</option>
                    <option value="nuccore">Nucleotide</option>
                    <option value="protein">Protein</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Max Results:</label>
                  <input 
                    v-model.number="ncbiSearchForm.max_results" 
                    type="number" 
                    min="1" 
                    max="100" 
                    class="form-control"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Sort By:</label>
                  <select v-model="ncbiSearchForm.sort_by" class="form-select">
                    <option value="relevance">Relevance</option>
                    <option value="pub_date">Publication Date</option>
                    <option value="author">Author</option>
                    <option value="journal">Journal</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div class="form-actions">
              <button 
                @click="executeNCBISearchModal"
                :disabled="searchingNCBI || !ncbiSearchForm.query"
                class="btn btn-primary"
              >
                <i class="fas fa-search" v-if="!searchingNCBI"></i>
                <i class="fas fa-spinner fa-spin" v-else></i>
                {{ searchingNCBI ? 'Searching...' : 'Search NCBI' }}
              </button>
            </div>
          </div>
          
          <!-- Search Results -->
          <div v-if="ncbiSearchResults" class="search-results mt-4">
            <h4>Search Results</h4>
            <div class="results-summary">
              <p>{{ ncbiSearchResults.success ? 'Search completed successfully' : 'Search failed' }}</p>
              <p v-if="ncbiSearchResults.response_time_ms">
                Response time: {{ ncbiSearchResults.response_time_ms }}ms
              </p>
            </div>
            
            <div class="results-data">
              <pre>{{ JSON.stringify(ncbiSearchResults.data, null, 2) }}</pre>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeNCBISearchModal" class="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- API Response Modal -->
    <div v-if="showResponseModal" class="modal-overlay" @click="closeResponseModal">
      <div class="modal-content large-modal" @click.stop>
        <div class="modal-header">
          <h3>API Response</h3>
          <button @click="closeResponseModal" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div v-if="apiResponse" class="response-content">
            <div class="response-summary">
              <div class="row">
                <div class="col-md-3">
                  <strong>Status:</strong> 
                  <span :class="apiResponse.success ? 'text-success' : 'text-danger'">
                    {{ apiResponse.success ? 'Success' : 'Failed' }}
                  </span>
                </div>
                <div class="col-md-3" v-if="apiResponse.response_time_ms">
                  <strong>Response Time:</strong> {{ apiResponse.response_time_ms }}ms
                </div>
                <div class="col-md-3" v-if="apiResponse.status_code">
                  <strong>Status Code:</strong> {{ apiResponse.status_code }}
                </div>
                <div class="col-md-3" v-if="apiResponse.cached">
                  <strong>Cached:</strong> 
                  <span class="text-info">Yes</span>
                </div>
              </div>
            </div>
            
            <div class="response-data mt-3">
              <h5>Response Data:</h5>
              <pre class="response-json">{{ JSON.stringify(apiResponse.data, null, 2) }}</pre>
            </div>
            
            <div v-if="!apiResponse.success && apiResponse.error" class="response-error mt-3">
              <h5>Error:</h5>
              <div class="alert alert-danger">
                {{ apiResponse.error }}
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeResponseModal" class="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

export default {
  name: 'APIIntegrations',
  setup() {
    const toast = useToast()
    
    // Reactive data
    const connections = ref([])
    const apiTemplates = ref([])
    const requestHistory = ref([])
    const analytics = ref(null)
    
    // Modal states
    const showCreateModal = ref(false)
    const showEditModal = ref(false)
    const showNCBISearchModal = ref(false)
    const showResponseModal = ref(false)
    const apiResponse = ref(null)
    const updatingConnection = ref(false)
    
    // Tab management
    const activeTab = ref('connections')
    const tabs = ref([
      { id: 'connections', name: 'Connections', icon: 'fas fa-plug' },
      { id: 'explorer', name: 'API Explorer', icon: 'fas fa-compass' },
      { id: 'history', name: 'Request History', icon: 'fas fa-history' },
      { id: 'analytics', name: 'Analytics', icon: 'fas fa-chart-line' },
      { id: 'github', name: 'GitHub', icon: 'fab fa-github' },
    ])

    // GitHub integration state
    const githubMappings = ref([])
    const invoicingClients = ref([])
    const loadingMappings = ref(false)
    const showAddMapping = ref(false)
    const savingMapping = ref(false)
    const newMapping = reactive({ repo_full_name: '', client_organization: '', display_name: '' })
    const githubConnectionExists = computed(() =>
      connections.value?.some((c) =>
        c.base_url?.includes('api.github.com') || c.name?.toLowerCase().includes('github')
      )
    )
    const githubTokenInput = ref('')
    const githubConnectionName = ref('GitHub')
    const savingGitHubToken = ref(false)
    const githubTokenError = ref('')
    const showGitHubTokenForm = ref(false)
    
    // Connection management
    const selectedConnection = ref(null)
    const testingConnections = ref([])
    const discoveringEndpoints = ref([])
    const creatingConnection = ref(false)
    const executingRequest = ref(false)
    const searchingNCBI = ref(false)
    const openDropdowns = ref([])
    
    // Forms
    const newConnection = reactive({
      name: '',
      description: '',
      base_url: '',
      auth_type: 'api_key',
      api_template: null,
      timeout_seconds: 30,
      credentials: {}
    })
    
    const ncbiForm = reactive({
      database: 'pubmed',
      query: '',
      max_results: 20
    })
    
    const apiForm = reactive({
      endpoint_path: '',
      method: 'GET',
      params_json: ''
    })
    
    const ncbiSearchForm = reactive({
      database: 'pubmed',
      query: '',
      max_results: 10,
      sort_by: 'relevance'
    })
    
    // Edit connection form
    const editConnectionForm = reactive({
      id: null,
      name: '',
      description: '',
      base_url: '',
      auth_type: 'api_key',
      api_template: null,
      timeout_seconds: 30,
      credentials: {},
      showApiKey: false
    })
    
    const historyFilter = reactive({
      connection: '',
      status: ''
    })
    
    const ncbiSearchResults = ref(null)
    
    // Computed properties
    const selectedTemplate = computed(() => {
      if (!newConnection.api_template) return null
      return apiTemplates.value.find(t => t.id === newConnection.api_template)
    })
    
    // Load data functions
    const loadConnections = async () => {
      try {
        const response = await api.get('/api-connections/')
        connections.value = response.data
      } catch (error) {
        console.error('Error loading API connections:', error)
        toast.error('Failed to load API connections')
      }
    }
    
    const loadAPITemplates = async () => {
      try {
        const response = await api.get('/api-templates/')
        apiTemplates.value = response.data
      } catch (error) {
        console.error('Error loading API templates:', error)
        toast.error('Failed to load API templates')
      }
    }
    
    const loadRequestHistory = async () => {
      try {
        const response = await api.get('/api-requests/')
        requestHistory.value = response.data
      } catch (error) {
        console.error('Error loading request history:', error)
        toast.error('Failed to load request history')
      }
    }
    
    const loadAnalytics = async () => {
      try {
        const response = await api.get('/api-requests/analytics/')
        analytics.value = response.data
      } catch (error) {
        console.error('Error loading analytics:', error)
        toast.error('Failed to load analytics')
      }
    }
    
    // Connection actions
    const createConnection = async () => {
      if (creatingConnection.value) return
      
      creatingConnection.value = true
      
      try {
        // Ensure credentials object exists and clean up empty values
        const credentials = { ...newConnection.credentials }
        
        // For NCBI connections, ensure email and tool are provided
        if (selectedTemplate.value && selectedTemplate.value.api_type === 'NCBI_EUTILS') {
          if (!credentials.email) {
            toast.error('Email is required for NCBI connections')
            return
          }
          if (!credentials.tool) {
            toast.error('Tool name is required for NCBI connections')
            return
          }
          // Remove empty API key if not provided
          if (!credentials.api_key || credentials.api_key.trim() === '') {
            delete credentials.api_key
          }
        }
        
        const connectionData = {
          ...newConnection,
          credentials: credentials
        }
        
        const response = await api.post('/api-connections/', connectionData)
        
        // Ensure the response has all the data we need
        const createdConnection = response.data
        console.log('Created connection:', createdConnection) // Debug log
        
        connections.value.push(createdConnection)
        toast.success('API connection created successfully')
        closeCreateModal()
        
        // Refresh the connections list to ensure we have the latest data
        await loadConnections()
        
      } catch (error) {
        console.error('Error creating connection:', error)
        
        // Show specific validation errors if available
        if (error.response && error.response.status === 400 && error.response.data) {
          const errors = error.response.data
          let errorMessage = 'Validation failed: '
          
          if (typeof errors === 'object') {
            const errorMessages = []
            for (const [field, fieldErrors] of Object.entries(errors)) {
              if (Array.isArray(fieldErrors)) {
                errorMessages.push(`${field}: ${fieldErrors.join(', ')}`)
              } else {
                errorMessages.push(`${field}: ${fieldErrors}`)
              }
            }
            errorMessage += errorMessages.join('; ')
          } else {
            errorMessage += errors
          }
          
          toast.error(errorMessage)
        } else {
          toast.error('Failed to create API connection')
        }
      } finally {
        creatingConnection.value = false
      }
    }
    
    const testConnection = async (connection) => {
      console.log('Testing connection:', connection) // Debug log
      
      if (!connection || !connection.id) {
        toast.error('Connection ID is missing. Please refresh the page and try again.')
        return
      }
      
      testingConnections.value.push(connection.id)
      
      try {
        const response = await api.post(`/api-connections/${connection.id}/test_connection/`)
        
        if (response.data.success) {
          toast.success(`Connection test successful for ${connection.name}`)
          // Update connection status
          connection.status = 'ACTIVE'
        } else {
          toast.error(`Connection test failed: ${response.data.error}`)
          connection.status = 'ERROR'
        }
      } catch (error) {
        console.error('Error testing connection:', error)
        toast.error('Failed to test connection')
        connection.status = 'ERROR'
      } finally {
        testingConnections.value = testingConnections.value.filter(id => id !== connection.id)
      }
    }
    
    const discoverEndpoints = async (connection) => {
      discoveringEndpoints.value.push(connection.id)
      
      try {
        const response = await api.post(`/api-connections/${connection.id}/discover_endpoints/`)
        
        if (response.data.success) {
          toast.success(`Endpoints discovered for ${connection.name}`)
          connection.discovered_endpoints = response.data.endpoints
        } else {
          toast.warning('No endpoints could be discovered automatically')
        }
      } catch (error) {
        console.error('Error discovering endpoints:', error)
        toast.error('Failed to discover endpoints')
      } finally {
        discoveringEndpoints.value = discoveringEndpoints.value.filter(id => id !== connection.id)
      }
    }
    
    const deleteConnection = async (connection) => {
      if (!confirm(`Are you sure you want to delete the connection "${connection.name}"?`)) {
        return
      }
      
      try {
        await api.delete(`/api-connections/${connection.id}/`)
        connections.value = connections.value.filter(c => c.id !== connection.id)
        toast.success('API connection deleted successfully')
      } catch (error) {
        console.error('Error deleting connection:', error)
        toast.error('Failed to delete API connection')
      }
    }
    
    // NCBI-specific functions
    const executeNCBISearch = async () => {
      if (!selectedConnection.value || !ncbiForm.query) return
      
      executingRequest.value = true
      
      try {
        const response = await api.post(`/api-connections/${selectedConnection.value.id}/ncbi_search/`, {
          database: ncbiForm.database,
          query: ncbiForm.query,
          max_results: ncbiForm.max_results
        })
        
        apiResponse.value = response.data
        showResponseModal.value = true
        
        if (response.data.success) {
          toast.success('NCBI search completed successfully')
        } else {
          toast.error(`NCBI search failed: ${response.data.error}`)
        }
      } catch (error) {
        console.error('Error executing NCBI search:', error)
        toast.error('Failed to execute NCBI search')
      } finally {
        executingRequest.value = false
      }
    }
    
    const executeNCBISearchModal = async () => {
      if (!selectedConnection.value || !ncbiSearchForm.query) return
      
      searchingNCBI.value = true
      
      try {
        const response = await api.post(`/api-connections/${selectedConnection.value.id}/ncbi_search/`, {
          database: ncbiSearchForm.database,
          query: ncbiSearchForm.query,
          max_results: ncbiSearchForm.max_results,
          sort_by: ncbiSearchForm.sort_by
        })
        
        ncbiSearchResults.value = response.data
        
        if (response.data.success) {
          toast.success('NCBI search completed successfully')
        } else {
          toast.error(`NCBI search failed: ${response.data.error}`)
        }
      } catch (error) {
        console.error('Error executing NCBI search:', error)
        toast.error('Failed to execute NCBI search')
      } finally {
        searchingNCBI.value = false
      }
    }
    
    const getNCBIDatabases = async () => {
      if (!selectedConnection.value) return
      
      executingRequest.value = true
      
      try {
        const response = await api.get(`/api-connections/${selectedConnection.value.id}/ncbi_databases/`)
        
        apiResponse.value = response.data
        showResponseModal.value = true
        
        if (response.data.success) {
          toast.success('NCBI databases retrieved successfully')
        } else {
          toast.error(`Failed to get NCBI databases: ${response.data.error}`)
        }
      } catch (error) {
        console.error('Error getting NCBI databases:', error)
        toast.error('Failed to get NCBI databases')
      } finally {
        executingRequest.value = false
      }
    }
    
    // Generic API functions
    const executeAPIRequest = async () => {
      if (!selectedConnection.value || !apiForm.endpoint_path) return
      
      executingRequest.value = true
      
      try {
        let params = {}
        if (apiForm.params_json) {
          params = JSON.parse(apiForm.params_json)
        }
        
        const response = await api.post(`/api-connections/${selectedConnection.value.id}/make_request/`, {
          endpoint_path: apiForm.endpoint_path,
          method: apiForm.method,
          params: params,
          purpose: 'manual_explorer_request'
        })
        
        apiResponse.value = response.data
        showResponseModal.value = true
        
        if (response.data.success) {
          toast.success('API request completed successfully')
        } else {
          toast.error(`API request failed: ${response.data.error}`)
        }
      } catch (error) {
        console.error('Error executing API request:', error)
        toast.error('Failed to execute API request')
      } finally {
        executingRequest.value = false
      }
    }
    
    // Utility functions
    const getAPIType = (connection) => {
      if (connection.api_template_name) {
        return connection.api_template_name
      }
      return 'Custom API'
    }
    
    const getDomainFromUrl = (url) => {
      try {
        return new URL(url).hostname
      } catch {
        return url
      }
    }
    
    const getAuthTypeDisplay = (authType) => {
      const types = {
        'none': 'None',
        'api_key': 'API Key',
        'bearer_token': 'Bearer Token',
        'basic_auth': 'Basic Auth',
        'oauth2': 'OAuth 2.0'
      }
      return types[authType] || authType
    }
    
    const getStatusText = (status) => {
      const statuses = {
        'ACTIVE': 'Active',
        'ERROR': 'Error',
        'PENDING': 'Pending',
        'RATE_LIMITED': 'Rate Limited',
        'INACTIVE': 'Inactive'
      }
      return statuses[status] || status
    }
    
    const getSuccessRate = (connection) => {
      if (!connection.request_count || connection.request_count === 0) return 0
      return Math.round((connection.successful_requests / connection.request_count) * 100)
    }
    
    const isNCBIConnection = (connection) => {
      return connection.api_template_name === 'NCBI E-utilities' || 
             (connection.base_url && connection.base_url.includes('eutils.ncbi.nlm.nih.gov'))
    }
    
    const formatTimestamp = (timestamp) => {
      return new Date(timestamp).toLocaleString()
    }
    
    // Modal functions
    const closeCreateModal = () => {
      showCreateModal.value = false
      Object.assign(newConnection, {
        name: '',
        description: '',
        base_url: '',
        auth_type: 'api_key',
        api_template: null,
        timeout_seconds: 30,
        credentials: {}
      })
    }
    
    const openNCBISearchModal = (connection) => {
      selectedConnection.value = connection
      showNCBISearchModal.value = true
    }
    
    const closeNCBISearchModal = () => {
      showNCBISearchModal.value = false
      ncbiSearchResults.value = null
      Object.assign(ncbiSearchForm, {
        database: 'pubmed',
        query: '',
        max_results: 10,
        sort_by: 'relevance'
      })
    }
    
    const closeResponseModal = () => {
      showResponseModal.value = false
      apiResponse.value = null
    }
    
    // UI functions
    const toggleDropdown = (connectionId) => {
      const index = openDropdowns.value.indexOf(connectionId)
      if (index > -1) {
        openDropdowns.value.splice(index, 1)
      } else {
        openDropdowns.value.push(connectionId)
      }
    }
    
    const viewConnectionStats = (connection) => { // eslint-disable-line no-unused-vars
      // Implementation for viewing detailed connection stats
      toast.info('Connection stats view coming soon')
    }
    
    const viewRequestHistory = (connection) => { // eslint-disable-line no-unused-vars
      // Implementation for viewing connection-specific request history
      activeTab.value = 'history'
      historyFilter.connection = connection.id
      loadRequestHistory()
    }
    
    const editConnection = async (connection) => {
      // Load the full connection details including credentials
      try {
        const response = await api.get(`/api-connections/${connection.id}/`)
        const fullConnection = response.data
        
        // Populate the edit form
        editConnectionForm.id = fullConnection.id
        editConnectionForm.name = fullConnection.name || ''
        editConnectionForm.description = fullConnection.description || ''
        editConnectionForm.base_url = fullConnection.base_url || ''
        editConnectionForm.auth_type = fullConnection.auth_type || 'api_key'
        editConnectionForm.api_template = fullConnection.api_template?.id || fullConnection.api_template || null
        editConnectionForm.timeout_seconds = fullConnection.timeout_seconds || 30
        editConnectionForm.showApiKey = false
        
        // Load credentials (the backend should return decrypted credentials for the owner)
        editConnectionForm.credentials = fullConnection.decrypted_credentials || fullConnection.credentials || {}
        
        // Close dropdown and show modal
        openDropdowns.value = []
        showEditModal.value = true
      } catch (error) {
        console.error('Error loading connection details:', error)
        toast.error('Failed to load connection details')
      }
    }
    
    const closeEditModal = () => {
      showEditModal.value = false
      editConnectionForm.id = null
      editConnectionForm.name = ''
      editConnectionForm.description = ''
      editConnectionForm.base_url = ''
      editConnectionForm.auth_type = 'api_key'
      editConnectionForm.api_template = null
      editConnectionForm.timeout_seconds = 30
      editConnectionForm.credentials = {}
      editConnectionForm.showApiKey = false
    }
    
    const updateConnection = async () => {
      if (!editConnectionForm.id) return
      
      updatingConnection.value = true
      try {
        const payload = {
          name: editConnectionForm.name,
          description: editConnectionForm.description,
          base_url: editConnectionForm.base_url,
          auth_type: editConnectionForm.auth_type,
          timeout_seconds: editConnectionForm.timeout_seconds
        }
        
        // Only include credentials if they have actual values (not empty strings)
        const hasCredentials = Object.entries(editConnectionForm.credentials).some(
          ([, value]) => value && value.toString().trim() !== ''
        )
        if (hasCredentials) {
          // Filter out empty values
          const filteredCredentials = {}
          for (const [key, value] of Object.entries(editConnectionForm.credentials)) {
            if (value && value.toString().trim() !== '') {
              filteredCredentials[key] = value
            }
          }
          payload.credentials = filteredCredentials
        }
        
        await api.patch(`/api-connections/${editConnectionForm.id}/`, payload)
        toast.success('Connection updated successfully')
        closeEditModal()
        loadConnections()
      } catch (error) {
        console.error('Error updating connection:', error)
        toast.error('Failed to update connection')
      } finally {
        updatingConnection.value = false
      }
    }
    
    const toggleApiKeyVisibility = () => {
      editConnectionForm.showApiKey = !editConnectionForm.showApiKey
    }
    
    const copyToClipboard = async (text) => {
      if (!text) {
        toast.warning('No value to copy')
        return
      }
      try {
        await navigator.clipboard.writeText(text)
        toast.success('Copied to clipboard')
      } catch (error) {
        console.error('Failed to copy:', error)
        toast.error('Failed to copy to clipboard')
      }
    }
    
    const viewRequestDetails = (request) => { // eslint-disable-line no-unused-vars
      // Implementation for viewing request details
      toast.info('Request details view coming soon')
    }
    
    // GitHub methods
    const saveGitHubToken = async () => {
      if (!githubTokenInput.value) return
      savingGitHubToken.value = true
      githubTokenError.value = ''
      try {
        // Delete ALL existing GitHub connections to avoid stale tokens being picked up
        const allGitHub = (connections.value || []).filter((c) =>
          c.base_url?.includes('api.github.com') || c.name?.toLowerCase().includes('github')
        )
        for (const conn of allGitHub) {
          await api.delete(`/api-connections/${conn.id}/`)
        }
        await api.post('/api-connections/', {
          name: githubConnectionName.value || 'GitHub',
          base_url: 'https://api.github.com',
          auth_type: 'bearer_token',
          description: 'GitHub API for invoice commit history',
          credentials: { token: githubTokenInput.value },
        })
        githubTokenInput.value = ''
        showGitHubTokenForm.value = false
        await loadConnections()
      } catch (e) {
        githubTokenError.value = e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed to save token.'
      } finally {
        savingGitHubToken.value = false
      }
    }

    const loadGitHubMappings = async () => {
      loadingMappings.value = true
      try {
        const res = await api.get('/github-audit/')
        githubMappings.value = res.data
      } catch (e) {
        console.error('Failed to load GitHub mappings', e)
      } finally {
        loadingMappings.value = false
      }
    }

    const loadInvoicingClients = async () => {
      try {
        const res = await api.get('/invoicing/clients/')
        invoicingClients.value = res.data
      } catch (e) {
        console.error('Failed to load invoicing clients', e)
      }
    }

    const saveMapping = async () => {
      if (!newMapping.repo_full_name || !newMapping.client_organization) return
      savingMapping.value = true
      try {
        await api.post('/github-audit/', { ...newMapping })
        newMapping.repo_full_name = ''
        newMapping.client_organization = ''
        newMapping.display_name = ''
        showAddMapping.value = false
        await loadGitHubMappings()
      } catch (e) {
        alert('Failed to save mapping: ' + (e.response?.data?.detail || e.message))
      } finally {
        savingMapping.value = false
      }
    }

    const deleteMapping = async (id) => {
      if (!confirm('Remove this repo mapping?')) return
      try {
        await api.delete(`/github-audit/${id}/`)
        await loadGitHubMappings()
      } catch (e) {
        alert('Failed to delete mapping')
      }
    }

    watch(activeTab, (tab) => {
      if (tab === 'github') {
        loadGitHubMappings()
        loadInvoicingClients()
      }
    })

    // Initialize
    onMounted(() => {
      loadConnections()
      loadAPITemplates()
      loadRequestHistory()
      loadAnalytics()
    })
    
    return {
      // Data
      connections,
      apiTemplates,
      requestHistory,
      analytics,
      
      // Modal states
      showCreateModal,
      showEditModal,
      showNCBISearchModal,
      showResponseModal,
      apiResponse,
      updatingConnection,
      
      // Tab management
      activeTab,
      tabs,

      // GitHub integration
      githubMappings,
      invoicingClients,
      loadingMappings,
      showAddMapping,
      savingMapping,
      newMapping,
      githubConnectionExists,
      githubTokenInput,
      githubConnectionName,
      savingGitHubToken,
      githubTokenError,
      showGitHubTokenForm,
      saveGitHubToken,
      saveMapping,
      deleteMapping,
      
      // Connection management
      selectedConnection,
      testingConnections,
      discoveringEndpoints,
      creatingConnection,
      executingRequest,
      searchingNCBI,
      openDropdowns,
      
      // Forms
      newConnection,
      ncbiForm,
      apiForm,
      ncbiSearchForm,
      historyFilter,
      ncbiSearchResults,
      editConnectionForm,
      
      // Computed
      selectedTemplate,
      
      // Functions
      loadConnections,
      loadAPITemplates,
      loadRequestHistory,
      loadAnalytics,
      createConnection,
      testConnection,
      discoverEndpoints,
      deleteConnection,
      executeNCBISearch,
      executeNCBISearchModal,
      getNCBIDatabases,
      executeAPIRequest,
      
      // Utility functions
      getAPIType,
      getDomainFromUrl,
      getAuthTypeDisplay,
      getStatusText,
      getSuccessRate,
      isNCBIConnection,
      formatTimestamp,
      
      // Modal functions
      closeCreateModal,
      closeEditModal,
      openNCBISearchModal,
      closeNCBISearchModal,
      closeResponseModal,
      
      // UI functions
      toggleDropdown,
      viewConnectionStats,
      viewRequestHistory,
      editConnection,
      updateConnection,
      toggleApiKeyVisibility,
      copyToClipboard,
      viewRequestDetails
    }
  }
}
</script>

<style scoped>
.api-integrations-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header Section */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #e9ecef;
}

.header-content h1 {
  color: #2c3e50;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.page-description {
  color: #6c757d;
  font-size: 1.1rem;
  margin-bottom: 0;
  max-width: 600px;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e9ecef;
}

.tab-button {
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  color: #6c757d;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
}

.tab-button:hover {
  color: #495057;
  background-color: #f8f9fa;
}

.tab-button.active {
  color: #007bff;
  border-bottom-color: #007bff;
  background-color: #fff;
}

.tab-button i {
  margin-right: 0.5rem;
}

/* Connection Cards */
.connections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.connection-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.connection-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.connection-card.status-active {
  border-color: #28a745;
}

.connection-card.status-error {
  border-color: #dc3545;
}

.connection-card.status-pending {
  border-color: #ffc107;
}

.connection-card.status-rate-limited {
  border-color: #fd7e14;
}

.connection-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.connection-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.connection-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.connection-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.detail-item {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.85rem;
}

.detail-item i {
  margin-right: 0.5rem;
  width: 12px;
}

/* Status Badges */
.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-error {
  background-color: #f8d7da;
  color: #721c24;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
}

.status-rate-limited {
  background-color: #ffeaa7;
  color: #d63031;
}

/* Connection Stats */
.connection-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.stat-value {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
}

/* Connection Actions */
.connection-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.connection-actions .btn {
  flex: 1;
  min-width: 80px;
}

/* Dropdown */
.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 160px;
  z-index: 1000;
  display: none;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  text-align: left;
  color: #212529;
  text-decoration: none;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger {
  color: #dc3545 !important;
}

.dropdown-divider {
  height: 0;
  margin: 0.5rem 0;
  overflow: hidden;
  border-top: 1px solid #dee2e6;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

.empty-state-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  color: #495057;
  margin-bottom: 1rem;
}

/* Forms */
.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}

.form-control, .form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus, .form-select:focus {
  border-color: #007bff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-outline-primary {
  border: 1px solid #007bff;
  color: #007bff;
  background: transparent;
}

.btn-outline-primary:hover {
  background-color: #007bff;
  color: #fff;
}

.btn-outline-info {
  border: 1px solid #17a2b8;
  color: #17a2b8;
  background: transparent;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  color: #fff;
}

.btn-outline-success {
  border: 1px solid #28a745;
  color: #28a745;
  background: transparent;
}

.btn-outline-success:hover {
  background-color: #28a745;
  color: #fff;
}

.btn-outline-secondary {
  border: 1px solid #6c757d;
  color: #6c757d;
  background: transparent;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: #fff;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.btn-lg {
  padding: 1rem 2rem;
  font-size: 1.125rem;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-content.large-modal {
  max-width: 900px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #495057;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

/* Tables */
.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.table th,
.table td {
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  text-align: left;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.endpoint-cell {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Method badges */
.method-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.method-get {
  background-color: #d4edda;
  color: #155724;
}

.method-post {
  background-color: #cce5ff;
  color: #004085;
}

.method-put {
  background-color: #fff3cd;
  color: #856404;
}

.method-delete {
  background-color: #f8d7da;
  color: #721c24;
}

/* Status badges for requests */
.status-success {
  background-color: #d4edda;
  color: #155724;
}

.status-error {
  background-color: #f8d7da;
  color: #721c24;
}

/* Analytics */
.analytics-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.analytics-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e9ecef;
}

.analytics-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.analytics-card .card-header h4 {
  margin: 0;
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
}

.analytics-card .card-header i {
  color: #007bff;
  font-size: 1.5rem;
}

.analytics-card .card-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.analytics-card .card-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
}

.chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e9ecef;
}

.chart-card h5 {
  margin-bottom: 1rem;
  color: #495057;
}

.connection-stats-list,
.endpoint-stats-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.connection-stat-item,
.endpoint-stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.connection-name,
.endpoint-name {
  font-weight: 500;
  color: #495057;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.connection-count,
.endpoint-count {
  font-weight: 600;
  color: #007bff;
  margin-left: 1rem;
}

/* Response display */
.response-content {
  max-height: 60vh;
  overflow-y: auto;
}

.response-summary {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.response-json {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Alerts */
.alert {
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.alert-info {
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

/* Utility classes */
.mr-2 { margin-right: 0.5rem; }
.mr-3 { margin-right: 1rem; }
.ml-2 { margin-left: 0.5rem; }
.mb-4 { margin-bottom: 1.5rem; }
.mt-3 { margin-top: 1rem; }
.mt-4 { margin-top: 1.5rem; }

.text-success { color: #28a745 !important; }
.text-danger { color: #dc3545 !important; }
.text-info { color: #17a2b8 !important; }

.row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -0.75rem;
}

.col-md-3,
.col-md-4,
.col-md-6,
.col-md-8 {
  padding: 0 0.75rem;
  flex: 0 0 auto;
}

.col-md-3 { width: 25%; }
.col-md-4 { width: 33.333333%; }
.col-md-6 { width: 50%; }
.col-md-8 { width: 66.666667%; }

@media (max-width: 768px) {
  .col-md-3,
  .col-md-4,
  .col-md-6,
  .col-md-8 {
    width: 100%;
    margin-bottom: 1rem;
  }
  
  .connections-grid {
    grid-template-columns: 1fr;
  }
  
  .analytics-cards {
    grid-template-columns: 1fr;
  }
  
  .header-section {
    flex-direction: column;
    gap: 1rem;
  }
  
  .connection-actions {
    flex-direction: column;
  }
  
  .connection-actions .btn {
    flex: none;
  }
}

/* ── GitHub Integration Tab ── */
.github-section { display: flex; flex-direction: column; gap: 20px; }
.github-section-header { display: flex; justify-content: space-between; align-items: flex-start; }
.github-section-header h3 { font-size: 1.2rem; font-weight: 700; margin-bottom: 4px; }
.section-subtitle { color: #6c7a9c; font-size: 0.875rem; margin: 0; }

.github-card {
  background: #fff;
  border: 1px solid #e0e4f0;
  border-radius: 10px;
  padding: 20px;
}

.github-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.github-card-title { font-size: 0.95rem; font-weight: 700; display: flex; align-items: center; gap: 8px; margin: 0 0 10px; }
.github-card-title i { color: #2c3e8c; }
.github-card-desc { font-size: 0.875rem; color: #555; margin-bottom: 14px; line-height: 1.6; }
.github-card-desc code { background: #f0f3ff; padding: 1px 5px; border-radius: 4px; font-size: 0.8rem; }

.btn-gh-primary {
  background: #2c3e8c;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: background 0.15s;
}
.btn-gh-primary:hover { background: #1e2d6e; }
.btn-gh-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-sm-gh { padding: 5px 12px; font-size: 0.8rem; }

.btn-gh-secondary {
  background: #f0f3ff;
  color: #2c3e8c;
  border: 1px solid #c5cdf5;
  border-radius: 7px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-gh-secondary:hover { background: #e0e4f8; }

.btn-gh-danger-sm {
  background: none;
  border: 1px solid #fce4ec;
  border-radius: 5px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #c62828;
  font-size: 0.75rem;
  transition: all 0.15s;
}
.btn-gh-danger-sm:hover { background: #fce4ec; }

.gh-connected-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  color: #2e7d32;
  font-size: 0.85rem;
  font-weight: 600;
}

.add-mapping-form {
  background: #f8f9ff;
  border: 1px dashed #c5cdf5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.mapping-form-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px; }
.mapping-field label { display: block; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #6c7a9c; margin-bottom: 4px; }
.gh-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e0e4f0;
  border-radius: 6px;
  font-size: 0.875rem;
  background: #fff;
}
.gh-input:focus { outline: none; border-color: #2c3e8c; }

.mapping-form-actions { display: flex; gap: 10px; }

.mappings-table-wrapper { overflow-x: auto; }
.mappings-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.mappings-table thead tr { background: #f8f9ff; }
.mappings-table thead th { padding: 8px 12px; text-align: left; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #6c7a9c; border-bottom: 1px solid #e0e4f0; }
.mappings-table tbody tr { border-bottom: 1px solid #f0f0f0; }
.mappings-table tbody tr:hover { background: #fafbff; }
.mappings-table tbody td { padding: 10px 12px; }
.mappings-table code { background: #f3e5f5; color: #6a1b9a; padding: 2px 6px; border-radius: 4px; font-size: 0.8rem; }

.active-dot { color: #2e7d32; font-weight: 600; font-size: 0.8rem; }
.inactive-dot { color: #999; font-size: 0.8rem; }

.gh-empty { text-align: center; color: #6c7a9c; padding: 20px; font-size: 0.875rem; }

.gh-token-form { }
.gh-token-summary { }
.gh-token-input-row { display: flex; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.gh-token-actions { display: flex; gap: 10px; align-items: center; }
.gh-link { color: var(--primary, #2c3e8c); text-decoration: none; font-weight: 500; }
.gh-link:hover { text-decoration: underline; }

.github-quick-link {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f0f3ff;
  border-color: #c5cdf5;
}
.github-quick-link i { font-size: 2rem; color: #2c3e8c; flex-shrink: 0; }
.github-quick-link strong { font-size: 0.95rem; display: block; margin-bottom: 4px; }
.github-quick-link p { font-size: 0.85rem; color: #555; margin: 0; }
.github-quick-link .btn-gh-primary { margin-left: auto; white-space: nowrap; }
</style>
